import type { LoopHandle } from './audio-engine.js';

/** What a bed needs from the audio engine (`AudioEngine` provides both). */
export interface BedBackend { decode(url: string): Promise<unknown>; loop(url: string, bus?: 'effects', trim?: LoopTrim, matched?: boolean): LoopHandle | undefined }
/** Seconds left off a recording's head and tail when it loops. */
export interface LoopTrim { head: number; tail: number }

/** Below this a bed is silent; its loop keeps running that way for `STOP_AFTER` seconds before it is stopped. */
const SILENT = .0005, STOP_AFTER = 5;

/**
 * One looping bed that follows a 0–1 level. It eases toward the level and fades all the way down rather than cutting out at a low
 * volume; only after a few seconds of silence does its loop stop, and it starts again (from the top) when it is next heard.
 */
export class AmbientBed {
  private level = 0;
  private quiet = 0;
  private requested = false;
  private handle: LoopHandle | undefined;

  constructor(private readonly backend: BedBackend, private readonly url: string, private readonly gain: number, private readonly trim?: LoopTrim) {}

  get playing() { return this.handle !== undefined; }

  update(target: number, master: number, seconds: number) {
    this.level += (target - this.level) * Math.min(1, seconds / 1.5);
    const volume = this.level * this.gain * master;
    if (volume < SILENT) {
      this.handle?.level(0);
      if (this.handle && (this.quiet += seconds) >= STOP_AFTER) { this.handle.stop(); this.handle = undefined; }
      return;
    }
    this.quiet = 0;
    if (!this.handle) {
      // Nothing downloads until the bed is first audible, so a night or rain bed that is never needed costs no bandwidth at startup.
      if (!this.requested) { this.requested = true; void this.backend.decode(this.url); }
      // Until it is decoded and the page has been touched there is no loop yet; the next tick tries again.
      this.handle = this.backend.loop(this.url, 'effects', this.trim, true);
    }
    this.handle?.level(volume);
  }
}
